import { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, Chip, Divider, Text } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { formatNumber } from '~/utils/common';
import { getReportInProgressCount } from '~/utils/delivery-agency/getReportInProgressCount';

import type {
  DeliveryResultReportSummary,
  ReportedDeliveryResult,
  ReportedShippingLabelStatus,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyReportSummary.module.scss';

const SUMMARY_ITEMS = [
  { label: '전체 운송장', className: 'total', textColor: 'kurly', valueKey: 'totalCount' },
  {
    label: '진행 중',
    className: 'in-progress',
    textColor: 'info',
    valueKey: 'IN_PROGRESS',
    breakdown: [
      { label: '출고 대기/진행중', valueKey: 'OUTBOUND_READY_OR_PROCESSING', dotClassName: 'dot-outbound' },
      { label: '배송 중', valueKey: 'ON_THE_WAY', dotClassName: 'dot-on-the-way' },
    ],
  },
  {
    label: '배송 완료',
    className: 'completed',
    textColor: 'success',
    valueKey: 'DELIVERY_COMPLETED',
    sub: [
      { label: '대응배송', valueKey: 'ALTERNATIVE_DELIVERY' },
      { label: '지연배송', valueKey: 'DELAYED' },
      { label: '전환배송', valueKey: 'CONVERTED_DELIVERY' },
    ],
  },
  { label: '배송 실패', className: 'failed', textColor: 'warning', valueKey: 'DELIVERY_FAILED' },
  { label: '배송 취소', className: 'canceled', textColor: 'tertiary', valueKey: 'DELIVERY_CANCELED' },
] as const;

type TypeSummaryItem = (typeof SUMMARY_ITEMS)[number];

export type TypeDeliveryAgencyReportSummaryValueKey = TypeSummaryItem['valueKey'];

export type TypeDeliveryAgencyReportSummarySubValueKey = Extract<
  TypeSummaryItem,
  { sub: readonly unknown[] }
>['sub'][number]['valueKey'];

/** SUMMARY_ITEMS의 valueKey가 응답 코드에서 벗어나면 조회 시점에 타입 에러로 드러나도록 키를 좁힌다. */
type TypeSummaryCountKey = ReportedShippingLabelStatus | ReportedDeliveryResult | 'totalCount' | 'IN_PROGRESS';

const buildCountByValueKey = (
  summary: DeliveryResultReportSummary | null
): Partial<Record<TypeSummaryCountKey, number>> => {
  const map: Partial<Record<TypeSummaryCountKey, number>> = { totalCount: summary?.totalCount ?? 0 };
  summary?.shippingLabelStatusCounts.forEach(({ code, count }) => (map[code] = count));
  summary?.deliveryResultCounts.forEach(({ code, count }) => (map[code] = count));
  map.IN_PROGRESS = getReportInProgressCount(summary?.shippingLabelStatusCounts);
  return map;
};

interface DeliveryAgencyReportSummaryProps {
  summary: DeliveryResultReportSummary | null;
  selectedValueKeys: TypeDeliveryAgencyReportSummaryValueKey[];
  selectedSubValueKeys: TypeDeliveryAgencyReportSummarySubValueKey[];
  onSelect: (valueKey: TypeDeliveryAgencyReportSummaryValueKey) => void;
  onToggleSub: (subValueKey: TypeDeliveryAgencyReportSummarySubValueKey) => void;
}

/**
 * [배송 결과 리포트] 서머리 컴포넌트
 *
 * @remarks
 * '진행 중' 카드는 좌측 텍스트 영역 클릭 시 2개 상태(출고 대기/진행중, 배송 중)로 리스트를 필터링하고,
 * 우측 화살표 클릭 시 상태별 카운트 breakdown을 아코디언으로 펼친다.
 */
const DeliveryAgencyReportSummary = ({
  summary,
  selectedValueKeys,
  selectedSubValueKeys,
  onSelect,
  onToggleSub,
}: DeliveryAgencyReportSummaryProps) => {
  const data = useMemo(() => buildCountByValueKey(summary), [summary]);

  const [isInProgressOpen, setIsInProgressOpen] = useState(false);

  return (
    <div className={styles.container}>
      {SUMMARY_ITEMS.map((item) => {
        if ('breakdown' in item) {
          return (
            <div
              key={item.valueKey}
              className={clsx(styles.box, styles[item.className], {
                [styles.active]: selectedValueKeys.includes(item.valueKey),
              })}
            >
              <button type="button" className={styles['in-progress-filter']} onClick={() => onSelect(item.valueKey)}>
                <Text size="large" weight="semi-bold" color={item.textColor}>
                  {item.label}
                </Text>
                <Text size="xxlarge" weight="semi-bold" color={item.textColor}>
                  {formatNumber(data[item.valueKey] ?? 0)}건
                </Text>
              </button>
              <Divider color="gray200" orientation="vertical" className={styles['in-progress-divider']} />
              <button
                type="button"
                className={styles['in-progress-toggle']}
                aria-label={isInProgressOpen ? '진행 중 상세 접기' : '진행 중 상세 펼치기'}
                aria-expanded={isInProgressOpen}
                onClick={() => setIsInProgressOpen((prev) => !prev)}
              >
                {isInProgressOpen ? (
                  <ChevronUpIcon size={20} color="#0566CD" />
                ) : (
                  <ChevronDownIcon size={20} color="#0566CD" />
                )}
              </button>
              {isInProgressOpen && (
                <div className={styles['in-progress-body']}>
                  {item.breakdown.map((breakdownItem) => (
                    <div key={breakdownItem.valueKey} className={styles['breakdown-row']}>
                      <span className={styles['breakdown-label']}>
                        <span className={clsx(styles['breakdown-dot'], styles[breakdownItem.dotClassName])} />
                        <Text size="small" color="secondary" element="span">
                          {breakdownItem.label}
                        </Text>
                      </span>
                      <Text size="small" weight="semi-bold" color="info" element="span">
                        {formatNumber(data[breakdownItem.valueKey] ?? 0)}건
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={item.valueKey}
            type="button"
            className={clsx(styles.box, styles[item.className], {
              [styles.active]: selectedValueKeys.includes(item.valueKey),
            })}
            onClick={() => onSelect(item.valueKey)}
          >
            <div className={styles.main}>
              <Text size="large" weight="semi-bold" color={item.textColor}>
                {item.label}
              </Text>
              <Text size="xxlarge" weight="semi-bold" color={item.textColor}>
                {formatNumber(data[item.valueKey] ?? 0)}건
              </Text>
            </div>
            {'sub' in item && (
              <>
                <Divider color="gray100" orientation="vertical" className={styles['sub-divider']} />
                <div className={styles['chip-wrap']}>
                  {item.sub.map((subItem) => (
                    <Chip
                      key={subItem.valueKey}
                      size="medium"
                      variant="stroke"
                      color="success"
                      active={selectedSubValueKeys.includes(subItem.valueKey)}
                      className={styles.chip}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleSub(subItem.valueKey);
                      }}
                    >
                      {subItem.label} {formatNumber(data[subItem.valueKey] ?? 0)}건
                    </Chip>
                  ))}
                </div>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

export { DeliveryAgencyReportSummary };
